import { type GroupedOptionsType } from '@atlaskit/select';
import { type IFilterValues } from '@atlassian/eoc-condition-filter';
import { type WeekDay } from '@atlassian/eoc-day-time-interval';

export type NotifyCondition = 'if-not-acked' | 'if-not-closed';
export type ResponderType = 'escalation' | 'schedule' | 'team' | 'user';
export type MatchConditionTypes = 'match-all' | 'match-any-condition' | 'match-all-conditions';

export interface TimezoneResponse {
	timezones: Timezone[];
}

export interface RoutingRuleProps {
	onRoutingRuleModalOpen?: () => void;
	schedules?: Partial<ScheduleEntity>[];
}

export interface Timezone {
	id: string;
	sName: string;
	lName: string;
	simpleName: string;
	offset: number;
}

export enum OperationIds {
	Matches = 0,
	Equals = 5,
	Contains = 1,
	StartsWith = 3,
	EndsWith = 4,
	ContainsKey = 6,
	ContainsValue = 7,
	GreaterThan = 8,
	LessThan = 9,
	IsEmpty = 10,
	EqualsIgnoreWhitespace = 11,
}

export enum MatchConditionType {
	'match-all' = 0,
	'match-any-condition' = 1,
	'match-all-conditions' = 2,
}

export enum Operation {
	Matches = 'matches',
	Contains = 'contains',
	StartsWith = 'starts-with',
	EndsWith = 'ends-with',
	Equals = 'equals',
	ContainsKey = 'contains-key',
	ContainsValue = 'contains-value',
	GreaterThan = 'greater-than',
	LessThan = 'less-than',
	IsEmpty = 'is-empty',
	EqualsIgnoreWhitespace = 'equals-ignore-whitespace',
}

export interface Condition {
	order?: number;
	customKey?: string;
	field: string;
	expectedValue: string;
	not: boolean;
	operation: OperationIds;
	fieldFreeForm?: boolean;
}

export interface Entity {
	id: string;
	name: string;
	conditionMatchType: number;
	conditions: Condition[];
	timeRestrictedInterval: TimeRestrictedInterval;
	ruleTarget: number;
	isDefault: boolean;
	verbalForm: string[];
	entityType?: string;
	entityId?: string;
	entityName?: string;
	timeZoneId?: string | null;
	deleted?: boolean;
}

export interface EscalationEntity {
	enabled: boolean;
	name: string;
	description: string;
	repeat: number;
	unAckWhileRepeating: boolean;
	closeAlertWhenRepeatsCompleted: boolean;
	maxRepeatCount: number;
	teamId?: string;
	rules: RuleType[];
	id: string;
	collapsed: boolean;
}

export interface TimeRestrictedInterval {
	enabled: boolean;
	timeRestrictions: TimeRestriction[];
	appliedTimeZone: string;
}

export interface TimeRestriction {
	startHour: number;
	startMinute: number;
	endHour: number;
	endMinute: number;
	startDay?: WeekDay;
	endDay?: WeekDay;
}

export interface RoutingRules {
	entities: Entity[];
	collapsed: boolean;
	hasEditRight: boolean;
	hasDeleteRight: boolean;
	hasSubscriptionRight: boolean;
}

export interface RuleType {
	type: ResponderType;
	time: number;
	notifyId: string;
	notifyType: string;
	notifyCondition: NotifyCondition;
}

export interface AvailableNotifyType {
	label: string;
	value: string;
	type: ResponderType;
	disabled: boolean;
}

export interface Schedules {
	entities: ScheduleEntity[];
	collapsed: boolean;
	hasEditRight: boolean;
	hasDeleteRight: boolean;
	hasSubscriptionRight: boolean;
}

export interface DefaultTimeZone {
	id: string;
	display: string;
}

export interface RoutingRuleConditionField {
	id: string;
	label: string;
	type: string;
	operations: OperationEntity[];
	defaultOperation: number;
	options?: Options;
}

export interface OperationEntity {
	id: number;
	name: string;
	order: number;
}

export interface Options {
	list: List[];
	defaultValue: string;
}

export interface List {
	value: string;
	label: string;
}

export interface ScheduleEntity {
	id: string;
	name: string;
	description: string;
	teamId: string;
	ownerTeamName: string;
	timezone: string;
	enabled: boolean;
	collapsed: boolean;
	rotations: any[];
}

export interface Escalations {
	entities: EscalationEntity[];
	collapsed: boolean;
	hasEditRight: boolean;
	hasDeleteRight: boolean;
	hasSubscriptionRight: boolean;
	hasRepeatRight: boolean;
	availableNotifyTypes: AvailableNotifyType[];
}

export interface RoutingRulesResponse {
	routingRules: RoutingRules;
	escalations: Escalations;
	/** @private
	 * @deprecated Don't use schedules from this request, it is not source of truth.
	 * When some one updated schedule, it will not be reflected here.
	 * Use schedules from properties instead of it.
	 * */
	schedules: Schedules;
	defaultTimeZone: DefaultTimeZone;
	routingRuleConditionFields: RoutingRuleConditionField[];
}

export interface ElseSelectOptionType {
	value: string;
	label: string;
	type: string;
	enabled?: boolean;
}

export type RouteOptionType = ElseSelectOptionType | null;
export type RouteOptionsType = GroupedOptionsType<RouteOptionType>;

interface BaseRoutingRuleFormValues {
	name: string;
	conditions: IFilterValues;
	timeRestrictedIntervalEnabled: boolean;
	timeRestrictedInterval: string;
	entity: ElseSelectOptionType;
	timeZoneId?: {
		label: string;
		value: string;
	};
}

interface BaseRoutingRuleFormValuesWithEveryDay extends BaseRoutingRuleFormValues {
	start: string;
	end: string;
}

interface BaseRoutingRuleFormValuesWithCustomizedDays extends BaseRoutingRuleFormValues {
	timeRestrictions: TimeRestriction[];
}

export type RoutingRuleFormValues = BaseRoutingRuleFormValues &
	BaseRoutingRuleFormValuesWithEveryDay &
	BaseRoutingRuleFormValuesWithCustomizedDays;

export interface CreateRoutingRuleParams {
	name: Entity['name'];
	conditionMatchType: Entity['conditionMatchType'];
	conditions: Entity['conditions'];
	timeRestrictedInterval: Entity['timeRestrictedInterval'];
	ruleTarget: Entity['ruleTarget'];
	entityId: Entity['entityId'];
	entityType: Entity['entityType'];
	timeZoneId?: Entity['timeZoneId'];
}

export interface UpdateRoutingRuleParams extends CreateRoutingRuleParams {
	id: Entity['id'];
	isDefault: boolean;
}

export interface ChangeOrderRoutingRuleParams {
	id: Entity['id'];
	targetIndex: number;
}
